import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  MenuItem,
  ListItemIcon,
  IconButton,
} from '@mui/material'
import GroupsIcon from '@mui/icons-material/Groups'
import { useCreateChatRoomMutation } from '@/core/services/chat-room'
import { useNavigate } from 'react-router-dom'
import { Staff } from '@/core/models/staff.model'
import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

type CreateGroupChatProps = {
  creatorId: string
  staffs: Staff[]
  onGroupCreated?: () => void
  networkId?: string
  refetchNetwork?: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  hideTrigger?: boolean
}

export function CreateGroupChat({ creatorId, staffs, onGroupCreated, networkId, open: openProp, onOpenChange, hideTrigger }: CreateGroupChatProps) {
  useLingui()
  const navigate = useNavigate()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [createChatRoom] = useCreateChatRoomMutation()

  const isControlled = typeof openProp === 'boolean'
  const open = isControlled ? !!openProp : uncontrolledOpen
  const setOpen = (next: boolean) => {
    if (isControlled) {
      onOpenChange?.(next)
    } else {
      setUncontrolledOpen(next)
    }
  }

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    setGroupName('')
    setSelectedUsers([])
  }

  const handleCreateGroup = async () => {
    try {
      const room = await createChatRoom({
        users: !networkId ? [...selectedUsers, creatorId] : [...staffs.map((s) => s._id), creatorId],
        name: groupName,
        createdBy: creatorId,
        isPrivate: false,
        network: networkId,
      }).unwrap()

      // Navigate to the newly created group so user can send the first message
      navigate(`/chat?type=group&id=${room._id}`)

      onGroupCreated?.()
      handleClose()
    } catch (error) {
      console.error('Error creating group:', error)
    }
  }

  return (
    <>
      {!hideTrigger && (
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            backgroundColor: 'info.main',
            color: 'white',
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            '& .MuiSvgIcon-root': { display: 'block' },
            '&:hover': {
              backgroundColor: 'info.dark',
            },
          }}
        >
          <GroupsIcon fontSize="small" />
        </IconButton>
      )}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Trans>Create group chat</Trans>
        </DialogTitle>

        <DialogContent>
          <TextField fullWidth label="Network name" value={groupName} onChange={(e) => setGroupName(e.target.value)} sx={{ mt: 2, mb: 3 }} />
          {!networkId && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                <Trans>Select members</Trans>
              </Typography>

              <FormGroup sx={{ maxHeight: 400, overflow: 'auto' }}>
                {staffs.map((staff) => (
                  <FormControlLabel
                    key={staff._id}
                    control={
                      <Checkbox
                        checked={selectedUsers.includes(staff._id)}
                        onChange={(e) => {
                          const userId = staff._id
                          setSelectedUsers((prev) => (e.target.checked ? [...prev, userId] : prev.filter((id) => id !== userId)))
                        }}
                      />
                    }
                    label={`${staff.userInfo.name} ${staff.userInfo.surname}`}
                  />
                ))}
              </FormGroup>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="warning">
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleCreateGroup} variant="contained" disabled={!groupName || (selectedUsers.length === 0 && !networkId)}>
            <Trans>Create</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
